<template>
  <div>
    <div class="form-group">
      <label>Scanner des références</label>
      <!-- QR-004 / QR-006 : les deux dépannages quand la douchette ne peut pas lire
           l'étiquette — saisir le code en clair, ou le lire avec l'appareil photo. La
           douchette reste le mode nominal, sélectionné par défaut. Même sélecteur que
           sur les autres écrans de scan, pour que le geste soit le même partout. -->
      <ScanModeSwitch v-model="scanMode" class="scan-mode" />
      <!-- QR-006 : en mode photo, le viseur prend la place du champ. Il n'en occupe que la
           hauteur utile, pour laisser voir le formulaire, et masquer le champ évite de faire
           surgir le clavier du mobile sur un écran où l'on ne tape pas. -->
      <CameraScanner v-if="cameraEntry" :inset="false" class="camera-inline"
                     @decoded="onCameraDecoded" @close="closeCameraScanner" />
      <b-input-group v-else>
        <input type="text" v-model="internal_code"
               :placeholder="manualEntry ? 'Saisir un code référence' : 'Scanner / ajouter une référence'"
               class="form-control scanning-field" ref="internal_code"
               @keyup.enter="submitManualEntry()" />
        <b-input-group-append v-if="manualEntry">
          <b-button variant="outline-secondary" :disabled="!internal_code" @click="submitManualEntry()">OK</b-button>
        </b-input-group-append>
      </b-input-group>
    </div>
    <AdminForm :config="config" :form="form" :isBusy="isBusy" :options="options"></AdminForm>
  </div>
</template>

<script>
import AdminForm from "@/components/Global/AdminForm";
import config from "./config";
import scannedValueMixin from "@/mixins/scannedValue";

export default {
  name: "BusinessReturnMobileForm",
  mixins: [scannedValueMixin],
  components: {
    AdminForm,
  },
  props: ["id_to_edit"],
  data() {
    return {
      config: config,
      internal_code: null,
      form: {
        agency_number: null,
        business_number: null,
        user_id: this.$store.getters["auth/id"],
        axiloc_creation_date: this.$moment().format("YYYY-MM-DD"),
        withdraw_wished_date: this.$moment().format("YYYY-MM-DD"),
        material_retrieving_place: 'axiloc',
        return_warehouse_id: (this.$store.getters["auth/warehouse"] || {}).id || null,
        unique_references: [],
        status_slug: 'business-return-transport-en-cours'
      },
      isBusy: false,
      inScanning: false,
      ready: false,
      options: {}
    };
  },
  watch: {
    urlQuery: function (query) {
      // Tant que l'état n'est pas restauré, on ne réécrit pas l'URL (on perdrait les paramètres)
      if (!this.ready || _.isEqual(query, this.$route.query)) return
      this.$router.replace({ query }).catch(() => {})
    },
    internal_code: function (internal_code) {
      // QR-004 : en saisie manuelle, la validation est explicite (bouton / Entrée).
      if (internal_code && !this.manualEntry) {
        // La douchette tape caractère par caractère : on attend la fin de la salve.
        this.handleScannedValueDebounced(internal_code);
      }
    }
  },
  computed: {
    // Références scannées et entrepôt choisi, reportés dans l'URL pour survivre à un rafraîchissement.
    urlQuery: function () {
      let query = {}
      let refs = this.form.unique_references
        .map(r => r.id)
        .filter(id => id != this.$route.params.id)
      if (refs.length) query.refs = refs.join(',')
      if (this.form.return_warehouse_id) query.warehouse = String(this.form.return_warehouse_id)
      return query
    }
  },
  mounted() {
    // État restauré depuis l'URL (rafraîchissement de la page)
    let query = this.$route.query
    // Un opérateur rattaché à un entrepôt ne peut déposer que dans le sien : l'URL ne le remplace pas
    let userWarehouse = this.$store.getters["auth/warehouse"]
    if (query.warehouse && !userWarehouse) {
      this.form.return_warehouse_id = parseInt(query.warehouse, 10)
    }
    let scannedIds = String(query.refs || '').split(',').filter(id => id && id != this.$route.params.id)

    // Chargement de la référence depuis laquelle on lance la création du retour
    // afin de l'ajouter par défaut au retour et de pré-remplir le formulaire.
    // Elle n'est dans le store que si l'on vient de sa fiche : sinon (URL directe, rafraîchissement) on la charge.
    this.loadReference(this.$route.params.id).then((reference) => {
      // Mouvement en cours (sans date de fin). Les logs arrivent du plus récent au plus ancien,
      // et un log sans affaire sérialise business/agency en [] (truthy) : on teste le numéro.
      let lastLog = reference ? _.cloneDeep((reference.reference_logs || []).find(log => !log.end_date)) : null
      if (!lastLog || !lastLog.business || !lastLog.business.number) {
        this.$toastr.error("Cette référence n'est pas en location : impossible de créer un retour à partir d'elle.");
        return
      }

      this.form.unique_references = [this.referenceLine(reference.id, reference.internal_code, reference.product.name)];

      this.form.agency_number   = lastLog.agency.number
      this.form.business_number = lastLog.business.number

      this.options = {
        ...this.options,
        agency_number_options: [{
          value: lastLog.agency.number,
          text: lastLog.agency.number + '-' + lastLog.agency.name
        }],
        business_number_options: [{
          value: lastLog.business.number,
          text: lastLog.business.number + '-' + lastLog.business.name
        }]
      }

      return Promise.all(scannedIds.map(id => this.loadReference(id))).then((references) => {
        references.filter(ref => ref).forEach((ref) => {
          this.form.unique_references.push(this.referenceLine(ref.id, ref.internal_code, ref.product.name))
        })
        this.ready = true
      })
    })

    this.$store.dispatch("warehouse/getAll", true).then(() => {
      this.options = {
        ...this.options,
        return_warehouse_id_options: this.$store.getters["warehouse/getAll"]
          .filter(warehouse => !userWarehouse || warehouse.id == userWarehouse.id)
          .selectOptions("id", ["internal_code", " - ", "name"]),
      }
    })
  },
  methods: {
    loadReference: function (id) {
      let reference = this.$store.getters["reference/getItem"](id)
      if (reference) return Promise.resolve(reference)
      return this.$store.dispatch("reference/getItem", { id: id, wait: true })
        .then(() => this.$store.getters["reference/getItem"](id))
        .catch(() => null)
    },
    referenceLine: function (id, internal_code, product_name) {
      return { id: id, internal_code: internal_code, name: internal_code + ' - ' + product_name }
    },
    // QR-003 : appelé par le mixin une fois la valeur normalisée.
    onScanResolved: function (code) {
      this.getReference(code);
    },
    /**
     * Cherche une référence à partir de son code barre
     * @param   {string|interger} internal_code  Code barre
     * @returns
     */
    getReference: function (internal_code) {
      if (this.inScanning == true) {
        this.internal_code = null;
        return
      }

      this.inScanning = true

      this.$store.dispatch("reference/getItemByInternalCode", internal_code).then((reference) => {
        if (!reference.id) {

          this.inScanning = false
          this.$toastr.error(
            "Le code barre scanné ne correspond à aucune référence"
          );
          return false
        }
        if (this.form.unique_references.find(r => r.internal_code == internal_code)) {

          this.inScanning = false
          this.$toastr.error(
            "La référence a déjà été scannée"
          );
          return false
        }
        if (reference.status != 'reference-en-location') {

          this.inScanning = false
          this.$toastr.error(
            "La référence scannée n'est pas actuellement en location, elle ne peut donc pas faire l'objet d'un retour"
          );
          return false
        }
        if(reference.business.number != this.form.business_number){
          this.inScanning = false
          this.$toastr.error(
            "La référence scannée n'est pas en location dans la même agence."
          );
          return false
        }

        // Ajout de la référence au retour
        this.form.unique_references.push(this.referenceLine(reference.id, reference.internal_code, reference.name));

        this.inScanning = false
      })
      this.internal_code = null;
    }
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/_variables.scss";

.scanning-field {
  margin-bottom: 20px;
  border: 1px solid $primary;
}

// Le sélecteur de mode occupe sa propre ligne, au-dessus du champ qu'il commande.
.scan-mode {
  margin-bottom: 10px;
}

// Cet écran est un formulaire, pas une barre d'actions : le viseur n'a pas de retrait
// horizontal à reprendre, et porte la marge basse du champ qu'il remplace.
.camera-inline {
  margin-bottom: 20px;
}
</style>



// WEBPACK FOOTER //
// src/components/BusinessReturnMobile/BusinessReturnMobileForm.vue